import React, { useState, useCallback, useEffect, useRef } from "react";
import "../styles/CodeHubStyles.css";
import { useApi } from "../../../service/ApiContext";
import Logger from "../../../utils/Logger";
import CodeMirror from "@uiw/react-codemirror";
import { python } from "@codemirror/lang-python";
import { javascript } from "@codemirror/lang-javascript";
import { java } from "@codemirror/lang-java";
import { oneDark } from "@codemirror/theme-one-dark";
import { indentWithTab } from "@codemirror/commands";
import { keymap } from "@codemirror/view";
import { EditorView } from "@codemirror/view";
import { autocompletion } from "@codemirror/autocomplete";
import { indentUnit } from "@codemirror/language";
import needHelpIcon from "../../../assets/images/LiveSession/needHelpIcon.svg";
import codePlayButton from "../../../assets/images/codeEdge/runCodeButton.svg";
import dropdownIcon from "../../../assets/images/codeEdge/dropdownIcon.svg";
import resetIcon from "../../../assets/images/codeEdge/resetIcon.svg";
import clock from "../../../assets/images/codeEdge/clock 3.svg";
import bulb from "../../../assets/images/codeEdge/idea 1.svg";
import runningPlaceholder from "../../../assets/images/codeEdge/coding 1.svg";
import CodeEdgeNeedHelp from "../components/CodeEdgeNeedHelp";

// Enum matching your backend

enum ProgrammingLanguage {
  python = "Python",
  java = "Java",
  javascript = "JavaScript",
}

// Interface for API request
interface CodeExecutionRequest {
  code_content: string;
  programming_language: ProgrammingLanguage;
}

// Interface for API response
interface CodeExecutionResponse {
  stdout: string;
  stderr: string;
  exitCode: number;
  timedOut: boolean;
  executionTime: number;
  phase: string;
}

// Interface for component output state
interface CodeOutput {
  stdout: string;
  stderr: string;
  exitCode?: number;
  executionTime?: number;
  timedOut?: boolean;
  error?: string;
}

// Language-specific settings
interface LanguageSettings {
  indentSize: number;
  indentChar: string;
  commentPrefix: string;
  blockStart: string;
  blockEnd?: string;
  autoClosePairs: Record<string, string>;
  keywords: string[];
  template: string;
}

// Language-specific extensions for CodeMirror
const getLanguageExtension = (language: ProgrammingLanguage) => {
  switch (language) {
    case ProgrammingLanguage.python:
      return python();
    case ProgrammingLanguage.javascript:
      return javascript({ jsx: false, typescript: false });
    case ProgrammingLanguage.java:
      return java();
    default:
      return python();
  }
};

const customTheme = EditorView.theme({
  "&": {
    height: "100%",
    fontSize: "14px",
    fontFamily: "'Fira Code', 'Courier New', monospace",
    backgroundColor: "#ffffff",
    color: "#1f2937",
  },

  ".cm-content": {
    padding: "12px 0",
    backgroundColor: "#ffffff",
    color: "#1f2937",
  },

  ".cm-line": {
    padding: "0 8px",
  },

  ".cm-gutters": {
    backgroundColor: "#f8fafc",
    color: "#64748b",
    borderRight: "1px solid #e2e8f0",
  },

  ".cm-activeLineGutter": {
    backgroundColor: "#eef2ff",
  },

  ".cm-activeLine": {
    backgroundColor: "#f8fafc",
  },

  ".cm-cursor": {
    borderLeft: "2px solid #395B9C",
  },

  ".cm-selectionBackground": {
    backgroundColor: "#dbeafe !important",
  },

  "&.cm-focused .cm-selectionBackground": {
    backgroundColor: "#bfdbfe !important",
  },

  ".cm-matchingBracket": {
    backgroundColor: "#dbeafe",
    borderBottom: "1px solid #93c5fd",
    color: "#1e293b !important",
  },

  ".cm-nonmatchingBracket": {
    backgroundColor: "#fecaca",
  },

  ".cm-tooltip": {
    backgroundColor: "#ffffff",
    border: "1px solid #d1d5db",
    color: "#111827",
  },

  ".cm-tooltip-autocomplete": {
    "& > ul > li[aria-selected]": {
      backgroundColor: "#395B9C",
      color: "#ffffff",
    },
  },

  "&.cm-focused": {
    outline: "none",
  },
});
// Custom theme for CodeMirror that matches your UI with proper selection styles
// const customTheme = EditorView.theme({
//   "&": {
//     height: "100%",
//     fontSize: "14px",
//     fontFamily: "'Fira Code', 'Courier New', monospace",
//     backgroundColor: "#ffffff",
//   },
//   ".cm-content": {
//     padding: "12px 0",
//     backgroundColor: "#ffffff",
//     color: "#d4d4d4",
//   },
//   ".cm-line": {
//     padding: "0 8px",
//   },
//   ".cm-gutters": {
//     backgroundColor: "#252526",
//     color: "#858585",
//     borderRight: "1px solid #3e3e42",
//   },
//   ".cm-activeLineGutter": {
//     backgroundColor: "#2a2d2e",
//   },
//   ".cm-activeLine": {
//     backgroundColor: "#2a2d2e",
//   },
//   // // Selection styles - fixed to show properly
//   // "&.cm-focused .cm-selectionBackground": {
//   //   backgroundColor: "#264f78 !important",
//   // },
//   // "& .cm-selectionBackground": {
//   //   backgroundColor: "#264f78 !important",
//   // },
//   // "&.cm-focused .cm-selectionMatch": {
//   //   backgroundColor: "#264f78 !important",
//   // },
//   // "& .cm-selectionMatch": {
//   //   backgroundColor: "#264f78 !important",
//   // },
//   ".cm-matchingBracket": {
//     backgroundColor: "#4a4a4a",
//     color: "#d4d4d4 !important",
//     borderBottom: "1px solid #6a6a6a",
//   },
//   ".cm-nonmatchingBracket": {
//     backgroundColor: "#a12525",
//   },
//   ".cm-tooltip": {
//     backgroundColor: "#252526",
//     border: "1px solid #454545",
//     color: "#d4d4d4",
//   },
//   ".cm-tooltip-autocomplete": {
//     "& > ul > li": {
//       borderColor: "#454545",
//     },
//     "& > ul > li[aria-selected]": {
//       backgroundColor: "#094771",
//       color: "white",
//     },
//   },
//   "&.cm-focused .cm-cursor": {
//     borderLeftColor: "#d4d4d4",
//   },
//   "&.cm-focused": {
//     outline: "none",
//   },
//   // Selection styles for when editor has focus
//   // "&.cm-focused .cm-selectionBackground": {
//   //   backgroundColor: "#264f78 !important",
//   // },

//   // // Selection styles even when editor doesn't have focus
//   // "& .cm-selectionBackground": {
//   //   backgroundColor: "#264f78 !important",
//   // },

//   // // Selection matches (when you select a word and it highlights other occurrences)
//   // "&.cm-focused .cm-selectionMatch": {
//   //   backgroundColor: "#264f78 !important",
//   // },

//   // // The selection layer
//   // ".cm-selectionLayer .cm-selectionBackground": {
//   //   backgroundColor: "#264f78 !important",
//   // },

//   // // Unfocused but still selected state (with slightly lower opacity)
//   // "&:not(.cm-focused) .cm-selectionBackground": {
//   //   backgroundColor: "#264f78 !important",
//   //   opacity: 0.7,
//   // },
// });

const CodeHubVersionTwo: React.FC = () => {
  const api = useApi();
  const editorRef = useRef<any>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<ProgrammingLanguage>(
    ProgrammingLanguage.python,
  );
  const [code, setCode] = useState<string>("");
  const [output, setOutput] = useState<CodeOutput>({ stdout: "", stderr: "" });
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [hasExecuted, setHasExecuted] = useState(false);
  // Language-specific settings
  const languageSettings: Record<ProgrammingLanguage, LanguageSettings> = {
    [ProgrammingLanguage.python]: {
      indentSize: 4,
      indentChar: " ",
      commentPrefix: "#",
      blockStart: ":",
      autoClosePairs: {
        "(": ")",
        "[": "]",
        "{": "}",
        '"': '"',
        "'": "'",
      },
      keywords: [
        "def",
        "class",
        "if",
        "elif",
        "else",
        "for",
        "while",
        "try",
        "except",
        "finally",
        "with",
        "import",
        "from",
        "as",
        "return",
        "yield",
        "break",
        "continue",
        "pass",
        "lambda",
        "True",
        "False",
        "None",
        "and",
        "or",
        "not",
        "in",
        "is",
      ],
      template: `# Python Code with Proper Indentation
def greet(name):
    \"\"\"Return a greeting message.\"\"\"
    return f"Hello, {name}!"

def calculate_sum(numbers):
    \"\"\"Calculate sum of numbers.\"\"\"
    total = 0
    for num in numbers:
        total += num
    return total

def main():
    # Test the functions
    name = "Code Hub User"
    print(greet(name))
    
    numbers = [1, 2, 3, 4, 5]
    result = calculate_sum(numbers)
    print(f"Sum of numbers: {result}")
    
    # List comprehension example
    squares = [x**2 for x in range(5)]
    print(f"Squares: {squares}")

if __name__ == "__main__":
    main()`,
    },

    [ProgrammingLanguage.java]: {
      indentSize: 4,
      indentChar: " ",
      commentPrefix: "//",
      blockStart: "{",
      blockEnd: "}",
      autoClosePairs: {
        "(": ")",
        "[": "]",
        "{": "}",
        '"': '"',
        "'": "'",
      },
      keywords: [
        "public",
        "private",
        "protected",
        "class",
        "interface",
        "extends",
        "implements",
        "static",
        "final",
        "void",
        "int",
        "String",
        "boolean",
        "double",
        "float",
        "long",
        "short",
        "byte",
        "char",
        "if",
        "else",
        "for",
        "while",
        "do",
        "switch",
        "case",
        "break",
        "continue",
        "return",
        "try",
        "catch",
        "finally",
        "throw",
        "throws",
        "new",
        "this",
        "super",
        "import",
        "package",
      ],
      template: `// Java Code with Proper Structure
public class Main {
    
    /**
     * Greets the user with a message.
     * @param name The name to greet
     * @return A greeting message
     */
    public static String greet(String name) {
        return "Hello, " + name + "!";
    }
    
    /**
     * Calculates the sum of an array of numbers.
     * @param numbers Array of integers
     * @return Sum of all numbers
     */
    public static int calculateSum(int[] numbers) {
        int total = 0;
        for (int num : numbers) {
            total += num;
        }
        return total;
    }
    
    public static void main(String[] args) {
        // Test the methods
        String name = "Code Hub User";
        System.out.println(greet(name));
        
        int[] numbers = {1, 2, 3, 4, 5};
        int result = calculateSum(numbers);
        System.out.println("Sum of numbers: " + result);
    }
}`,
    },

    [ProgrammingLanguage.javascript]: {
      indentSize: 2,
      indentChar: " ",
      commentPrefix: "//",
      blockStart: "{",
      blockEnd: "}",
      autoClosePairs: {
        "(": ")",
        "[": "]",
        "{": "}",
        '"': '"',
        "'": "'",
        "`": "`",
      },
      keywords: [
        "function",
        "const",
        "let",
        "var",
        "if",
        "else",
        "for",
        "while",
        "do",
        "switch",
        "case",
        "break",
        "continue",
        "return",
        "try",
        "catch",
        "finally",
        "throw",
        "new",
        "this",
        "class",
        "extends",
        "super",
        "import",
        "export",
        "default",
        "async",
        "await",
        "typeof",
        "instanceof",
        "in",
        "of",
        "yield",
        "delete",
      ],
      template: `// JavaScript Code with Modern Syntax
// Function to greet user
function greet(name) {
    return \`Hello, \${name}!\`;
}

// Arrow function example
const calculateSum = (numbers) => {
    return numbers.reduce((total, num) => total + num, 0);
};

// Class example
class Calculator {
    constructor() {
        this.name = 'Code Hub Calculator';
    }
    
    multiply(a, b) {
        return a * b;
    }
    
    divide(a, b) {
        if (b === 0) throw new Error('Division by zero');
        return a / b;
    }
}

// Main execution
function main() {
    const name = "Code Hub User";
    console.log(greet(name));
    
    const numbers = [1, 2, 3, 4, 5];
    const result = calculateSum(numbers);
    console.log(\`Sum of numbers: \${result}\`);
    
    // Array methods example
    const doubled = numbers.map(n => n * 2);
    console.log(\`Doubled numbers: \${doubled}\`);
    
    // Using the Calculator class
    const calc = new Calculator();
    console.log(\`5 * 3 = \${calc.multiply(5, 3)}\`);
}

main();`,
    },
  };

  // Reverse map for display
  const displayLanguageMap: Record<ProgrammingLanguage, string> = {
    [ProgrammingLanguage.python]: "Python",
    [ProgrammingLanguage.java]: "Java",
    [ProgrammingLanguage.javascript]: "JavaScript",
  };

  // Initialize with Python template
  useEffect(() => {
    setCode(languageSettings[ProgrammingLanguage.python].template);
  }, []);

  // Handle language change
  // const handleLanguageChange = (
  //   event: React.ChangeEvent<HTMLSelectElement>,
  // ) => {
  //   const newLanguage = event.target.value as ProgrammingLanguage;
  //   setSelectedLanguage(newLanguage);
  //   setCode(languageSettings[newLanguage].template);
  //   setOutput({ stdout: "", stderr: "" });
  // };

  const changeLanguage = (newLanguage: ProgrammingLanguage) => {
    setSelectedLanguage(newLanguage);

    setCode(languageSettings[newLanguage].template);

    setOutput({
      stdout: "",
      stderr: "",
    });

    setHasExecuted(false);

    setIsDropdownOpen(false);
  };

  const handleLanguageChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    changeLanguage(event.target.value as ProgrammingLanguage);
  };

  // Handle code change
  const handleCodeChange = (value: string) => {
    setCode(value);
  };

  // Execute code using your API with useApi hook
  const runCode = useCallback(async () => {
    if (!code.trim()) {
      setOutput({
        stdout: "",
        stderr: "",
        error: "No code to execute",
      });
      return;
    }

    setIsRunning(true);
    setHasExecuted(true);
    setOutput({ stdout: "", stderr: "", error: "Running..." });

    const requestBody: CodeExecutionRequest = {
      code_content: code,
      programming_language: selectedLanguage,
    };

    try {
      Logger.info("Executing code:", {
        language: selectedLanguage,
        codeLength: code.length,
      });

      const response = await api.post<CodeExecutionResponse>(
        "/programming/run",
        requestBody,
      );
      const data = response.data;

      Logger.info("Code execution response:", data);

      setOutput({
        stdout: data.stdout,
        stderr: data.stderr,
        exitCode: data.exitCode,
        executionTime: data.executionTime,
        timedOut: data.timedOut,
      });

      if (data.exitCode === 0) {
        Logger.info("Code executed successfully", {
          executionTime: data.executionTime,
          phase: data.phase,
        });
      } else {
        Logger.warn("Code executed with errors", {
          exitCode: data.exitCode,
          stderr: data.stderr,
          timedOut: data.timedOut,
        });
      }
    } catch (error: any) {
      Logger.error("Error executing code:", error);

      let errorMessage = "Failed to execute code. ";

      if (error.response) {
        errorMessage += `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage +=
          "No response from server. Please check your connection.";
      } else {
        errorMessage += error.message || "An unexpected error occurred";
      }

      setOutput({
        stdout: "",
        stderr: "",
        error: errorMessage,
      });
    } finally {
      setIsRunning(false);
    }
  }, [code, selectedLanguage, api]);

  // Clear output
  const clearOutput = () => {
    setOutput({ stdout: "", stderr: "" });
    Logger.info("Output cleared");
  };

  // Reset to template
  const resetToTemplate = () => {
    setCode(languageSettings[selectedLanguage].template);
    setOutput({ stdout: "", stderr: "" });
    Logger.info("Reset to template for:", selectedLanguage);
    setHasExecuted(false);
  };

  // Format output for display
  const renderOutput = () => {
    if (output.error) {
      return (
        <div className="codehub-error-container">
          <div className="codehub-error-icon">
            <i className="fas fa-exclamation-circle"></i>
          </div>
          <pre className="codehub-error-text">{output.error}</pre>
        </div>
      );
    }

    const hasStdout = output.stdout && output.stdout.trim() !== "";
    const hasStderr = output.stderr && output.stderr.trim() !== "";

    if (!hasStdout && !hasStderr && output.exitCode === 0) {
      return (
        <div className="codehub-success-container">
          <i className="fas fa-check-circle"></i>
          <pre className="codehub-output-text">
            ✅ Code executed successfully with no output.
          </pre>
        </div>
      );
    }

    return (
      <>
        {hasStdout && (
          <div className="codehub-output-block">
            <pre className="codehub-output-text">{output.stdout}</pre>
          </div>
        )}

        {hasStderr && (
          <div className="codehub-output-block">
            <div className="codehub-output-label codehub-error-label">
              <i className="fas fa-exclamation-circle"></i> Error:
            </div>
            <pre className="codehub-error-text">{output.stderr}</pre>
          </div>
        )}
      </>
    );
  };

  // Get language icon
  const getLanguageIcon = (language: ProgrammingLanguage) => {
    switch (language) {
      case ProgrammingLanguage.python:
        return "fa-python";
      case ProgrammingLanguage.java:
        return "fa-java";
      case ProgrammingLanguage.javascript:
        return "fa-js";
      default:
        return "fa-code";
    }
  };

  // Get language color
  const getLanguageColor = (language: ProgrammingLanguage) => {
    switch (language) {
      case ProgrammingLanguage.python:
        return "#3776AB";
      case ProgrammingLanguage.java:
        return "#007396";
      case ProgrammingLanguage.javascript:
        return "#F7DF1E";
      default:
        return "#667eea";
    }
  };

  const [isModalOpen, setModalOpen] = useState(false);

  const needHelptoggleModal = () => {
    setModalOpen(!isModalOpen);
    if (!isModalOpen) {
      // logActionEvent(
      //   analytics_strings.analytics_events.dashboard_skill_needHelp,
      //   {
      //     candidate_id: userData.candidate_id,
      //   },
      // );
    }
  };

  // Get placeholder text (used as initial hint)
  const getPlaceholder = (language: ProgrammingLanguage) => {
    switch (language) {
      case ProgrammingLanguage.python:
        return "# Start typing your Python code here...";
      case ProgrammingLanguage.java:
        return "// Start typing your Java code here...";
      case ProgrammingLanguage.javascript:
        return "// Start typing your JavaScript code here...";
      default:
        return `// Start typing your code here...`;
    }
  };
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  return (
    <div className="codehub-container">
      <div
        style={{
          display: "flex",
          alignItems: "start",
          justifyContent: "space-between",
          boxShadow: "0 4px 8px rgba(105, 103, 103, 0.1)", // Bottom box shadow
          padding: "20px",
          height: "12vh",
          zIndex: 10000,
          marginBottom: "10px",
        }}
        className="v2-codeHubTitleHeader"
      >
        <div className="v2-codeHubTitleHeaderLeft">
          <div className="v2-codeHubTitleText">Code Edge</div>
          <div className="v2-codeHubTitleSubText">
            Hey Learner, Lets code smarter with hands-on learning experiences
          </div>
        </div>
        <div className="v2-codeHubTitleHeaderRight">
          <img
            src={needHelpIcon}
            alt=""
            style={{ height: "35px", width: "35px", cursor: "pointer" }}
            onClick={needHelptoggleModal}
          />
        </div>
      </div>

      {/* <hr className="v2-codeHubDivider " /> */}

      {/* Header Section */}
      {/* <div className="codehub-header">
        <div className="codehub-controls">
          <div className="codehub-language-selector">
            <label htmlFor="language-select" className="codehub-select-label">
              <i className="fas fa-globe"></i>
              Language:
            </label>
            <select
              id="language-select"
              className="codehub-select"
              value={selectedLanguage}
              onChange={handleLanguageChange}
              style={{ borderColor: getLanguageColor(selectedLanguage) }}
            >
              {Object.values(ProgrammingLanguage).map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
            </select>
          </div>

          <div className="codehub-action-buttons">
            <button
              className="codehub-btn codehub-btn-primary"
              onClick={runCode}
              disabled={isRunning}
              style={{
                background: `linear-gradient(135deg, ${getLanguageColor(selectedLanguage)} 0%, #764ba2 100%)`,
              }}
            >
              <i
                className={`fas ${isRunning ? "fa-spinner fa-spin" : "fa-play"}`}
              ></i>
              {isRunning ? "Running..." : "Run Code"}
            </button>
            <button
              className="codehub-btn codehub-btn-secondary"
              onClick={resetToTemplate}
              title="Reset to template"
            >
              <i className="fas fa-undo"></i>
              Reset
            </button>
          </div>
        </div>
      </div> */}

      {/* Main Content */}
      <div className="codehub-main">
        {/* Code Editor Section */}
        <div className="codehub-editor-section">
          <div className="codehub-section-header">
            {/* <span className="codehub-section-title">
              <i
                className={`fab ${getLanguageIcon(selectedLanguage)}`}
                style={{ color: getLanguageColor(selectedLanguage) }}
              ></i>
              {displayLanguageMap[selectedLanguage]} Editor
            </span> */}
            <div className="codehub-language-selector">
              <div
                className="codehub-custom-select"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <span>{selectedLanguage}</span>
                <img
                  src={dropdownIcon}
                  alt="dropdown arrow"
                  className={`codehub-dropdown-arrow ${
                    isDropdownOpen ? "open" : ""
                  }`}
                />{" "}
              </div>

              {isDropdownOpen && (
                <div className="codehub-custom-options">
                  {Object.values(ProgrammingLanguage).map((lang) => (
                    <div
                      key={lang}
                      className={`codehub-custom-option ${
                        selectedLanguage === lang ? "selected" : ""
                      }`}
                      // onClick={() => {
                      //   setSelectedLanguage(lang);
                      //   setIsDropdownOpen(false);
                      // }}
                      onClick={() => changeLanguage(lang)}
                    >
                      {lang}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* <select
              id="language-select"
              className="codehub-select"
              value={selectedLanguage}
              onChange={handleLanguageChange}
              style={{ borderColor: getLanguageColor(selectedLanguage) }}
            >
              {Object.values(ProgrammingLanguage).map((lang) => (
                <option key={lang} value={lang} className="codehub-options">
                  {lang}
                </option>
              ))}
            </select> */}
            <div className="codehub-editor-info">
              <span className="codehub-line-count">
                <i className="fas fa-code"></i> {code.split("\n").length} lines
              </span>
              <img
                src={codePlayButton}
                onClick={() => {
                  if (!isRunning) {
                    runCode();
                  }
                }}
                alt=""
                style={{
                  height: "36px",
                  width: "35px",
                  cursor: "pointer",
                  opacity: isRunning ? 0.5 : 1,
                }}
              />
            </div>
          </div>

          {/* CodeMirror Editor */}
          <div
            className="codehub-codemirror-wrapper"
            style={{
              height: "100%",
              overflow: "auto",
              backgroundColor: "#ffffff",
            }}
          >
            <CodeMirror
              key={`editor-${selectedLanguage}`}
              value={code}
              height="100%"
              // theme={oneDark}
              extensions={[
                getLanguageExtension(selectedLanguage),
                keymap.of([indentWithTab]),
                autocompletion(),
                EditorView.lineWrapping,
                indentUnit.of("    "),
                customTheme,
              ]}
              onChange={handleCodeChange}
              basicSetup={{
                lineNumbers: true,
                highlightActiveLineGutter: true,
                highlightSpecialChars: true,
                history: true,
                foldGutter: true,
                drawSelection: true,
                dropCursor: true,
                allowMultipleSelections: true,
                indentOnInput: true,
                syntaxHighlighting: true,
                bracketMatching: true,
                closeBrackets: true,
                autocompletion: true,
                rectangularSelection: true,
                crosshairCursor: true,
                highlightActiveLine: true,
                highlightSelectionMatches: true,
                closeBracketsKeymap: true,
                defaultKeymap: true,
                searchKeymap: true,
                historyKeymap: true,
                foldKeymap: true,
                completionKeymap: true,
              }}
            />
          </div>

          <div className="codehub-editor-footer">
            <span className="codehub-tip">
              <img
                src={bulb}
                alt=""
                style={{ height: "15px", width: "15px" }}
              />
              Tip: Tab to indent • Shift+Tab to unindent • Enter for auto-indent
              • Auto-close brackets
            </span>
          </div>
        </div>

        {/* Output Section */}
        <div className="codehub-output-section">
          <div className="codehub-section-header">
            <span className="codehub-section-title">
              {/* <i className="fas fa-terminal"></i> */}
              Output
            </span>
            <span className="codehub-stat">
              <img
                src={clock}
                alt=""
                style={{ height: "16px", width: "16px" }}
              />
              Time: {output.executionTime || 0}ms
              {output.timedOut && (
                <span className="codehub-timeout-badge">
                  <i className="fas fa-exclamation-triangle"></i> Timed Out
                </span>
              )}
              {hasExecuted && (
                <img
                  src={resetIcon}
                  alt="Reset"
                  style={{
                    height: "36px",
                    width: "28px",
                    cursor: "pointer",
                  }}
                  onClick={resetToTemplate}
                />
              )}
            </span>
          </div>
          {/* <div
            className={`codehub-output ${
              output.stderr
                ? "codehub-output-error"
                : output.stdout
                  ? "codehub-output-success"
                  : ""
            }`}
          >
            {renderOutput()}
          </div> */}
          <div
            className={`codehub-output ${
              output.stderr
                ? "codehub-output-error"
                : output.stdout
                  ? "codehub-output-success"
                  : ""
            }`}
          >
            {!hasExecuted ? (
              <div className="codehub-output-placeholder">
                <div className="codehub-placeholder-play">
                  <img
                    src={codePlayButton}
                    alt="Run Code"
                    className="codehub-placeholder-play-icon"
                  />
                </div>

                <div className="codehub-placeholder-text">Click Run Code</div>

                <div className="codehub-placeholder-subtext">
                  to execute your program
                </div>
              </div>
            ) : isRunning ? (
              <div className="codehub-output-placeholder">
                <img
                  src={runningPlaceholder}
                  alt=""
                  style={{ height: "70px", width: "70px" }}
                />
                <div className="codehub-placeholder-text">Running Code...</div>

                {/* <div className="codehub-placeholder-subtext">
                  Please wait while your program executes
                </div> */}
              </div>
            ) : (
              renderOutput()
            )}
          </div>
        </div>
      </div>
      <CodeEdgeNeedHelp
        isOpen={isModalOpen}
        toggleModal={needHelptoggleModal}
      />
    </div>
  );
};

export default CodeHubVersionTwo;
